import { forwardRef, useMemo } from "react";
import { Layout, Layouts, Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import { Icon } from "@web-app/components";
import type { Chart } from "@web-app/types";

import ChartRenderer from "./components/ChartRenderer";

const ResponsiveGridLayout = WidthProvider(Responsive);

interface DashboardGridProps {
  charts: Chart[];
  dateRange: { from: string; to: string };
  isEditMode?: boolean;
  onDuplicateChart?: (chart: Chart) => void;
  onEditChart?: (chart: Chart) => void;
  onLayoutChange?: (
    chartId: string | null,
    newPosition: { h: number; w: number; x: number; y: number } | null,
    fullLayout?: Chart[],
  ) => void;
}

const DashboardGrid = forwardRef<HTMLDivElement, DashboardGridProps>(
  function DashboardGrid(
    {
      charts,
      dateRange,
      isEditMode = false,
      onDuplicateChart,
      onEditChart,
      onLayoutChange,
    },
    ref,
  ) {
    // Convert chart data to React Grid Layout format
    const layouts = useMemo(() => {
      const layout = charts.map((chart) => ({
        h: chart.gridPosition.h,
        i: chart.id,
        // Metric charts can be smaller (1x1 min), others 2x2 min
        minH: chart.type === "metric" ? 1 : 2,
        minW: chart.type === "metric" ? 1 : 2,
        static: false,
        w: chart.gridPosition.w,
        x: chart.gridPosition.x,
        y: chart.gridPosition.y,
      }));

      return {
        lg: layout,
        md: layout,
        sm: layout.map((item) => ({ ...item, w: Math.min(item.w, 6) })),
        xs: layout.map((item) => ({ ...item, w: Math.min(item.w, 4) })),
        xxs: layout.map((item) => ({ ...item, w: 2 })),
      };
    }, [charts]);

    const compactType = "horizontal" as const;

    const handleLayoutChange = (_: Layout[], allLayouts: Layouts) => {
      if (!isEditMode || !onLayoutChange) return;

      // Convert RGL layout back to our chart format
      const currentBreakpoint = getCurrentBreakpoint();
      const currentLayout = allLayouts[currentBreakpoint] || allLayouts.lg;

      const updatedCharts = charts.map((chart) => {
        const layoutItem = currentLayout.find(
          (item: Layout) => item.i === chart.id,
        );
        if (layoutItem) {
          return {
            ...chart,
            gridPosition: {
              h: layoutItem.h,
              w: layoutItem.w,
              x: layoutItem.x,
              y: layoutItem.y,
            },
          };
        }
        return chart;
      });

      onLayoutChange(null, null, updatedCharts);
    };

    const getCurrentBreakpoint = () => {
      if (typeof window === "undefined") return "lg";
      const width = window.innerWidth;
      if (width >= 1200) return "lg";
      if (width >= 996) return "md";
      if (width >= 768) return "sm";
      if (width >= 480) return "xs";
      return "xxs";
    };

    if (charts.length === 0) {
      return (
        <div className="flex items-center justify-center h-64 mx-6 mt-6 border-2 border-dashed border-gray-300 rounded-lg">
          <div className="text-center">
            <Icon name="chartMixedLight" className="w-8 h-8 text-gray-500" />
            <p className="text-gray-500 text-lg mb-2">
              No charts in this dashboard
            </p>
            <p className="text-gray-400 text-sm">Add a chart to get started</p>
          </div>
        </div>
      );
    }

    return (
      <div className="w-full" ref={ref}>
        <div
          className="relative"
          style={{
            backgroundImage: `
            linear-gradient(to right, #f3f4f6 1px, transparent 1px),
            linear-gradient(to bottom, #f3f4f6 1px, transparent 1px)
          `,
            backgroundSize: `${(120 * 12) / 12}px 120px`,
          }}
        >
          <ResponsiveGridLayout
            className="layout"
            layouts={layouts}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
            rowHeight={120}
            isDraggable={isEditMode}
            isResizable={isEditMode}
            compactType={compactType}
            preventCollision={!compactType}
            onLayoutChange={handleLayoutChange}
            useCSSTransforms={true}
            transformScale={1}
            margin={[-1, -1]}
            containerPadding={[0, 0]}
            resizeHandles={["se"]}
            draggableHandle={isEditMode ? undefined : ".no-drag"}
          >
            {charts.map((chart) => (
              <div
                key={chart.id}
                className={`group/chart w-full h-full bg-white overflow-hidden ${
                  isEditMode
                    ? "cursor-grab active:cursor-grabbing"
                    : "cursor-default"
                }`}
                style={{
                  border: "0.5px solid #e5e7eb",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {isEditMode && (onEditChart || onDuplicateChart) && (
                  <div className="absolute top-2 right-2 z-10 flex gap-1 opacity-0 group-hover/chart:opacity-100 transition-opacity">
                    {onDuplicateChart && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onDuplicateChart(chart);
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onTouchStart={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        className="px-1 bg-white rounded border border-gray-300 hover:bg-gray-50 cursor-pointer"
                        title="Duplicate chart"
                      >
                        <Icon
                          name="copy"
                          className="h-3.5 w-3.5 text-gray-600"
                        />
                      </button>
                    )}
                    {onEditChart && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onEditChart(chart);
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onTouchStart={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        className="px-1 bg-white rounded border border-gray-300 hover:bg-gray-50 cursor-pointer"
                        title="Edit chart"
                      >
                        <Icon
                          name="fasPencil"
                          className="h-3.5 w-3.5 text-gray-600"
                        />
                      </button>
                    )}
                  </div>
                )}
                <ChartRenderer chart={chart} dateRange={dateRange} />
              </div>
            ))}
          </ResponsiveGridLayout>
        </div>
      </div>
    );
  },
);

export default DashboardGrid;
